import { Component, Injector, Input, SimpleChanges, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UserAccountService } from '../../../common/service/user-account.service';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { BuildingPropertyTaxAssessment } from '../../model/building-property-tax-assesment';
import { PropertyFormCommandHandler } from '../../service/command/property-form-command-handler.service';
import { BuildingPropertyTaxAssessmentService } from '../../service/data/building-property-tax-assessment.service';
import { Building } from '../../model/building';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { DataService } from '../../../core/service/base/data.service';
import { PropertyHelpTextService } from '../../service/helpText/property-help-text.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { Money } from '../../../core/model/money';
import { Currencies, CurrencyPipe } from '../../../core/pipe/currency.pipe';

@Component({
    selector: 'app-building-property-tax-assessment',
    templateUrl: './building-property-tax-assessment.component.html',
    styleUrls: ['./building-property-tax-assessment.component.scss'],
    providers: [
        DeleteFormCommandHandler,
        PropertyFormCommandHandler,
        PropertyHelpTextService,
        { provide: DataService, useExisting: BuildingPropertyTaxAssessmentService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BuildingPropertyTaxAssessmentComponent extends FormComponentBase<BuildingPropertyTaxAssessment> {
  constructor(
    private _cf: FormControlExFactory,
    private _fb: FormBuilder,
    private _dataService: BuildingPropertyTaxAssessmentService,
    private _cdf: ColumnDefinitionFactory,
    private _actionWindowService: ActionWindowService,
    private _injector: Injector,
    private _nativeWindow: NativeWindowService,
    private _userAccountService: UserAccountService,
    private currencyPipe: CurrencyPipe,
    activatedRoute: ActivatedRoute,
    dispatcher: CommandDispatcher,
    handlerResolver: PropertyFormCommandHandler,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, _dataService, messageService, navigation, dispatcher, handlerResolver);
  }

  @Input() buildings: Building[];
  @Input() propertyTaxIds: string[] = [];
  @Output() assessmentsChange = new EventEmitter<{
    assessments: BuildingPropertyTaxAssessment[];
    removedAssessmentIds: string[];
    hasChanges: boolean;
  }>();
  @Input() buildingPropertyTaxAssessments: BuildingPropertyTaxAssessment[] = [];
  @Input() disabledBuildingIds: string[] = [];

  private selectedBuildingIds: string[] = [];
  buildingsSelection: SelectionItem[] = [];
  private pendingAssessments: BuildingPropertyTaxAssessment[] | null = null;
  private removedAssessmentIds: string[] = [];
  private lastDisabledKey: string | null = null;
  private suppressAssessmentChangeTracking = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['buildings'] || changes['disabledBuildingIds']) {
      const key = (this.disabledBuildingIds || []).map(String).sort().join('|');
      if (key !== this.lastDisabledKey || changes['buildings']) {
        this.lastDisabledKey = key;
        this.rebuildBuildingsSelection();
      }
    }
    if (changes['propertyTaxIds']) {
      // When propertyTaxIds change, reseed if we already have assessments
      if (this.form && this.buildingPropertyTaxAssessments && this.buildingPropertyTaxAssessments.length > 0) {
        this.seedExistingAssessments(this.buildingPropertyTaxAssessments, false);
      } else if (this.form) {
        this.emitAssessments(false);
      }
    }
    if (changes['buildingPropertyTaxAssessments']) {
      const assessmentsList = this.buildingPropertyTaxAssessments ?? [];
      // If form not initialized yet, defer seeding
      if (!this.form) {
        this.pendingAssessments = assessmentsList;
      } else {
        const allNew = assessmentsList.every(a => !(a as any)?.id);
        if (allNew && this.buildingForms?.length > 0) {
          // Ignore reseed triggered by our own child emissions for new rows
          return;
        }
        this.seedExistingAssessments(assessmentsList, false);
      }
    }
  }

  override ngOnInit(): void {
    // Initialize immediately without waiting for parent route params
    this.init(this.createModel());
    // Seed from any pending assessments provided before init
    if (this.pendingAssessments && this.pendingAssessments.length > 0) {
      this.seedExistingAssessments(this.pendingAssessments, false);
      this.pendingAssessments = null;
    } else if (this.buildingPropertyTaxAssessments && this.buildingPropertyTaxAssessments.length > 0) {
      this.seedExistingAssessments(this.buildingPropertyTaxAssessments, false);
    }
  }

  protected createModel(): BuildingPropertyTaxAssessment {
    return new BuildingPropertyTaxAssessment();
  }

  protected getFormGroup(): FormGroup {
    var formGroup = this._fb.group(
      {
        id: this._cf.create('PROPERTY_TAX.ASSESSMENT_ID', this.dataContext.id),
        propertyTaxBuildings: this._cf.createMandatory('BUILDING_LIST.HEADER', []),
        premisesLandTaxableValue: this._cf.create(
          'PROPERTY_TAX.TOTAL_SUM_BUILDING_DWELLING_AND_PREMISES',
          this.dataContext.premisesLandTaxableValue?.amount
        ),
        dwellingsLandTaxableValue: this._cf.create(
          'PROPERTY_TAX.TOTAL_SUM_BUILDING_LAND',
          this.dataContext.dwellingsLandTaxableValue?.amount
        ),
        buildingForms: this._fb.array([]),
      },
      { updateOn: 'change' }
    );
    // Ensure new forms are editable
    Object.values(formGroup.controls).forEach(ctrl => ctrl.enable({ emitEvent: false }));
    return formGroup;
  }

  protected init(data: BuildingPropertyTaxAssessment) {
    super.init(data);
    this.AddSubscription(
      this.buildingForms.valueChanges.subscribe(() => {
        if (!this.suppressAssessmentChangeTracking) {
          this.emitAssessments(true);
        }
      })
    );
    this.AddSubscription(
      this.form.controls['propertyTaxBuildings'].valueChanges.subscribe(val => {
        const valueArray = Array.isArray(val) ? val : val ? [val] : [];
        const normalizedBuildingIds = valueArray
          .map(value => {
            if (value == null) return null;
            if (typeof value === 'string' || typeof value === 'number') return String(value);
            // Handle SelectionItem or Building object shapes
            const mappedId = (value as any).value ?? (value as any).id ?? (value as any).buildingId ?? (value as any).number;
            return mappedId != null ? String(mappedId) : null;
          })
          .filter((mappedValue): mappedValue is string => !!mappedValue);
        const uniqueBuildingIds = Array.from(new Set(normalizedBuildingIds));
        this.selectedBuildingIds = uniqueBuildingIds;
        this.syncBuildingForms(uniqueBuildingIds);
        this.emitAssessments(true);
      })
    );
    // Emit assessments whenever any building form field changes
    const buildingFormsArray = this.form.get('buildingForms') as FormArray;
    this.AddSubscription(
      buildingFormsArray.valueChanges.subscribe(() => {
        if (!this.suppressAssessmentChangeTracking) {
          this.emitAssessments(true);
        }
      })
    );
  }

  get buildingForms(): FormArray {
    return this.form.controls['buildingForms'] as FormArray;
  }

  private createBuildingFormGroup(buildingId: string, assessment?: BuildingPropertyTaxAssessment): FormGroup {
    const group = this._fb.group(
      {
        id: this._cf.create('PROPERTY_TAX.ASSESSMENT_ID', (assessment as any)?.id ?? null),
        buildingId: this._cf.createMandatory('PROPERTY_TAX.BUILDING_ID', buildingId),
        propertyTaxValueYear: this._cf.createMandatory('PROPERTY_TAX.VALUE_YEAR', assessment?.valueYear),
        propertyTaxSurface: this._cf.createMandatory('PROPERTY_TAX.SURFACE', assessment?.surface),
        propertyTaxArea: this._cf.createMandatory('PROPERTY_TAX.AREA', assessment?.area),
        premisesTaxableValue: this._cf.create('PROPERTY_TAX.PREMISES_TAX_VALUE', assessment?.premisesTaxableValue?.amount),
        landPremisesTaxableValue: this._cf.create('PROPERTY_TAX.LAND_PREMISES_TAX_VALUE', assessment?.premisesLandTaxableValue?.amount),
        totalPremisesTaxableValue: this._cf.createReadonly('PROPERTY_TAX.TOTAL_PREMISES_TAX_VALUE', null),
        dwellingsTaxableValue: this._cf.create('PROPERTY_TAX.DWELLINGS_TAX_VALUE', assessment?.dwellingsTaxableValue?.amount),
        landDwellingsTaxableValue: this._cf.create('PROPERTY_TAX.LAND_DWELLINGS_TAX_VALUE', assessment?.dwellingsLandTaxableValue?.amount),
        totalDwellingsTaxableValue: this._cf.createReadonly('PROPERTY_TAX.TOTAL_DWELLINGS_TAX_VALUE', null),
      },
      { updateOn: 'change' }
    );

    // Keep row totals in sync when editing new or existing assessments
    this.updateTotalsForBuilding(group);
    const premControls = ['premisesTaxableValue', 'landPremisesTaxableValue'];
    premControls.forEach(ctrl => group.get(ctrl)?.valueChanges.subscribe(() => this.updateTotalsForBuilding(group)));
    const dwellControls = ['dwellingsTaxableValue', 'landDwellingsTaxableValue'];
    dwellControls.forEach(ctrl => group.get(ctrl)?.valueChanges.subscribe(() => this.updateTotalsForBuilding(group)));

    return group;
  }

  private updateTotalsForBuilding(group: FormGroup): void {
    const prem = Number(group.get('premisesTaxableValue')?.value ?? 0);
    const landPrem = Number(group.get('landPremisesTaxableValue')?.value ?? 0);
    const dwell = Number(group.get('dwellingsTaxableValue')?.value ?? 0);
    const landDwell = Number(group.get('landDwellingsTaxableValue')?.value ?? 0);

    const safePrem = (Number.isFinite(prem) ? prem : 0) + (Number.isFinite(landPrem) ? landPrem : 0);
    const safeDwell = (Number.isFinite(dwell) ? dwell : 0) + (Number.isFinite(landDwell) ? landDwell : 0);

    group.get('totalPremisesTaxableValue')?.setValue(safePrem, { emitEvent: false });
    group.get('totalDwellingsTaxableValue')?.setValue(safeDwell, { emitEvent: false });
  }

  private syncBuildingForms(selectedIds: string[]): void {
    const existingBuildingIds: string[] = [];
    for (let index = 0; index < this.buildingForms.length; index++) {
      const buildingFormGroup = this.buildingForms.at(index) as FormGroup;
      const buildingId = String(buildingFormGroup.controls['buildingId'].value);
      existingBuildingIds.push(buildingId);
    }
    // Remove deselected
    for (let index = this.buildingForms.length - 1; index >= 0; index--) {
      const buildingId = String((this.buildingForms.at(index) as FormGroup).controls['buildingId'].value);
      if (selectedIds.indexOf(buildingId) === -1) {
        const assessmentId = (this.buildingForms.at(index) as FormGroup).get('id')?.value;
        if (assessmentId && this.removedAssessmentIds.indexOf(assessmentId) === -1) {
          this.removedAssessmentIds.push(assessmentId);
        }
        this.buildingForms.removeAt(index);
      }
    }
    // Add newly selected
    for (const buildingId of selectedIds) {
      if (existingBuildingIds.indexOf(buildingId) === -1) {
        // Preserve existing assessment (id/values) if we already had one for this building
        const existingAssessment = (this.buildingPropertyTaxAssessments || []).find(a => {
          const candidateId = (a as any).buildingId ?? (a as any).building?.id ?? (a as any).buildingNumber;
          return String(candidateId) === String(buildingId);
        });
        this.buildingForms.push(this.createBuildingFormGroup(buildingId, existingAssessment));
      }
    }
    this.emitAssessments(true);
  }

  removeBuildingAt(index: number): void {
    if (index < 0 || index >= this.buildingForms.length) return;
    const group = this.buildingForms.at(index) as FormGroup;
    const assessmentId = group.get('id')?.value;
    const buildingId = String(group.get('buildingId')?.value ?? '');
    if (assessmentId) {
      this.addRemovedAssessmentId(String(assessmentId));
    }
    this.buildingForms.removeAt(index);
    this.selectedBuildingIds = this.selectedBuildingIds.filter(id => id !== buildingId);
    this.form?.controls['propertyTaxBuildings']?.setValue(this.selectedBuildingIds, { emitEvent: false });
    this.emitAssessments(true);
  }

  private seedExistingAssessments(assessmentsList: BuildingPropertyTaxAssessment[], hasChanges: boolean): void {
    this.removedAssessmentIds = [];
    // Build map unique by buildingId
    const assessmentsByBuilding = new Map<string, BuildingPropertyTaxAssessment>();
    const filteredAssessments = (assessmentsList || []).filter(assessmentItem => {
      // If propertyTaxIds are provided, only include matching BuildingPropertyTaxAssessments
      return !this.propertyTaxIds || this.propertyTaxIds.length === 0
        ? true
        : this.propertyTaxIds.map(String).includes(String(assessmentItem.propertyTaxId));
    });
    for (const assessmentItem of filteredAssessments) {
      const buildingId = String(
        (assessmentItem as any).buildingId ?? (assessmentItem as any).building?.id ?? (assessmentItem as any).buildingNumber ?? ''
      );
      if (!buildingId) continue;
      if (!assessmentsByBuilding.has(buildingId)) {
        assessmentsByBuilding.set(buildingId, assessmentItem);
      }
    }
    const buildingIds = Array.from(assessmentsByBuilding.keys());
    this.selectedBuildingIds = buildingIds;
    this.suppressAssessmentChangeTracking = true;
    try {
      // Set selection without triggering sync, rebuilding manually
      this.form.controls['propertyTaxBuildings'].setValue(buildingIds, { emitEvent: false });
      const buildingFormsArray = this.buildingForms;
      buildingFormsArray.clear();
      for (const buildingId of buildingIds) {
        const seeded = assessmentsByBuilding.get(buildingId);
        const grp = this.createBuildingFormGroup(buildingId, seeded);
        buildingFormsArray.push(grp);
      }
    } finally {
      this.suppressAssessmentChangeTracking = false;
    }
    this.emitAssessments(hasChanges);
  }

  private addRemovedAssessmentId(id: string): void {
    if (!id) return;
    const idStr = String(id);
    if (this.removedAssessmentIds.indexOf(idStr) === -1) {
      this.removedAssessmentIds.push(idStr);
    }
  }

  getBuildingLabel(id: string): string {
    const buildingsArray = this.buildings || [];
    for (const buildingItem of buildingsArray) {
      const buildingIdCandidate: any = (buildingItem as any).id ?? (buildingItem as any).buildingId ?? (buildingItem as any).number;
      if (String(buildingIdCandidate) === String(id)) {
        const buildingDisplayName: any =
          (buildingItem as any).name ?? (buildingItem as any).buildingName ?? (buildingItem as any).description ?? buildingIdCandidate;
        return String(buildingDisplayName);
      }
    }
    return String(id);
  }

  trackByGroupId = (index: number, control: any) => String((control as FormGroup)?.controls?.['buildingId']?.value ?? index);

  private rebuildBuildingsSelection(): void {
    const disabled = new Set((this.disabledBuildingIds || []).map(id => String(id)));
    const buildingsArray = this.buildings ?? [];
    this.buildingsSelection = buildingsArray.map(buildingItem => {
      const buildingId = String((buildingItem as any).id ?? (buildingItem as any).buildingId ?? (buildingItem as any).number);
      const buildingName = this.getBuildingLabel(buildingId);
      const isDisabled = disabled.has(buildingId);
      return { value: buildingId, name: buildingName, disabled: isDisabled } as SelectionItem;
    });
    // Keep current selection intact without triggering valueChanges loop
    this.form?.controls['propertyTaxBuildings']?.setValue(this.selectedBuildingIds, { emitEvent: false });
  }

  private emitAssessments(hasChanges: boolean): void {
    const assessmentsToEmit: BuildingPropertyTaxAssessment[] = [];
    if (!this.buildingForms) {
      this.assessmentsChange.emit({ assessments: assessmentsToEmit, removedAssessmentIds: [...this.removedAssessmentIds], hasChanges });
      return;
    }
    const propertyTaxId = this.propertyTaxIds && this.propertyTaxIds.length > 0 ? this.propertyTaxIds[0] : null;
    for (let index = 0; index < this.buildingForms.length; index++) {
      const buildingFormGroup = this.buildingForms.at(index) as FormGroup;
      const assessment = new BuildingPropertyTaxAssessment();
      assessment.id = buildingFormGroup.controls['id'].value;
      assessment.propertyTaxId = propertyTaxId;
      assessment.buildingId = String(buildingFormGroup.controls['buildingId'].value);
      assessment.valueYear = Number(buildingFormGroup.controls['propertyTaxValueYear'].value);
      assessment.surface = Number(buildingFormGroup.controls['propertyTaxSurface'].value);
      assessment.area = Number(buildingFormGroup.controls['propertyTaxArea'].value);
      assessment.premisesTaxableValue = this.currencyPipe.toMoney(
        buildingFormGroup.controls['premisesTaxableValue'].value,
        Currencies.getDefaultCurrency()
      );
      assessment.premisesLandTaxableValue = this.currencyPipe.toMoney(
        buildingFormGroup.controls['landPremisesTaxableValue'].value,
        Currencies.getDefaultCurrency()
      );
      assessment.totalPremisesTaxableValue = this.currencyPipe.toMoney(
        buildingFormGroup.controls['totalPremisesTaxableValue'].value,
        Currencies.getDefaultCurrency()
      );
      assessment.dwellingsTaxableValue = this.currencyPipe.toMoney(
        buildingFormGroup.controls['dwellingsTaxableValue'].value,
        Currencies.getDefaultCurrency()
      );
      assessment.dwellingsLandTaxableValue = this.currencyPipe.toMoney(
        buildingFormGroup.controls['landDwellingsTaxableValue'].value,
        Currencies.getDefaultCurrency()
      );
      assessment.totalDwellingsTaxableValue = this.currencyPipe.toMoney(
        buildingFormGroup.controls['totalDwellingsTaxableValue'].value,
        Currencies.getDefaultCurrency()
      );

      assessmentsToEmit.push(assessment);
    }
    this.assessmentsChange.emit({ assessments: assessmentsToEmit, removedAssessmentIds: [...this.removedAssessmentIds], hasChanges });
  }
}
